import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getDeviceId, getDeviceLabel } from '@/lib/deviceId';
import {
  authUrlWithNotice,
  purgeStoredSession,
  setSessionNotice,
  type SessionNotice,
} from '@/lib/singleSession';

// Keeps last_seen_at fresh and doubles as the fallback check for when the
// realtime message never arrives (dropped socket, sleeping laptop).
const HEARTBEAT_MS = 45_000;

// Routine handover tracing, useful when testing two browsers side by side and
// noise in production. Anomalies use console.warn and are always reported.
const trace = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.info('[SingleSession]', ...args);
};

export type SingleSessionStatus =
  /** No signed-in user. */
  | 'idle'
  /** Claim in flight — protected routes hold rendering until this resolves. */
  | 'checking'
  /** This device holds the account. */
  | 'active';

const SingleSessionContext = createContext<SingleSessionStatus>('idle');

/**
 * Enforces one active session per user, newest-login-wins.
 *
 * Signing in always succeeds and takes the account over. The device that held it
 * finds out over realtime (or on its next heartbeat) and returns to /auth with
 * an explanation.
 *
 * All tabs of one browser share a device id, so a second tab is not a second
 * session.
 */
export function SingleSessionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<SingleSessionStatus>('idle');
  const doneRef = useRef(false);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      doneRef.current = false;
      setStatus('idle');
      return;
    }

    const deviceId = getDeviceId();
    let cancelled = false;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    // Only true once the server has confirmed this device owns the row. Until
    // then, finding another device in the row means our claim didn't land — and
    // under newest-login-wins the fix is to claim again, never to sign out.
    let holdsClaim = false;
    // Server timestamp of our claim, read back from the row. Newest-login-wins
    // means we only step aside for a claim that is strictly newer than ours.
    let claimedAt: string | null = null;

    setStatus('checking');

    // Send the displaced device back to sign-in. Hard navigation so any live
    // roleplay socket, recorder or timer is torn down with the document.
    const bounceToAuth = async (notice: SessionNotice) => {
      if (cancelled || doneRef.current) return;
      doneRef.current = true;
      trace('returning to sign-in:', notice);
      setSessionNotice(notice);
      try {
        // scope: 'local' — a global sign-out would also revoke the tokens of the
        // device that just took the session over.
        await supabase.auth.signOut({ scope: 'local' });
      } catch (err) {
        console.warn('[SingleSession] sign-out failed:', err);
      }

      // The takeover already revoked this session server-side, so that logout
      // can come back 403 and leave the session in storage. Verify, and delete
      // it ourselves if it survived — otherwise this device walks straight back
      // into the app and re-claims the account.
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          const removed = purgeStoredSession();
          console.warn('[SingleSession] sign-out left the session behind; purged storage', {
            removed,
          });
        }
      } catch (err) {
        console.warn('[SingleSession] session check failed:', err);
      }

      window.location.assign(authUrlWithNotice(notice));
    };

    // Reads the row back. After a claim it records our claim timestamp; on a
    // dispute it says who really holds the account and since when.
    const readRow = async (context: string) => {
      const { data, error } = await supabase
        .from('active_user_sessions')
        .select('device_id, device_label, claimed_at, last_seen_at')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.warn('[SingleSession] row read failed:', context, error.message);
        return null;
      }

      trace('row', context + ':', data, { deviceId });

      if (context === 'after claim') {
        if (data?.device_id === deviceId) {
          claimedAt = data.claimed_at;
        } else {
          // The claim reported success but the row says otherwise — a write from
          // another device landed on top of ours between the two calls.
          console.warn('[SingleSession] claim did not stick:', { deviceId, row: data });
          holdsClaim = false;
        }
      }

      return data;
    };

    const claim = async () => {
      const { data, error } = await supabase.rpc('claim_active_session', {
        p_device_id: deviceId,
        p_device_label: getDeviceLabel(),
        p_user_agent: navigator.userAgent.slice(0, 500),
      });

      if (cancelled) return false;

      if (error) {
        // Fail open. A broken claim call must not lock people out of the app.
        console.warn('[SingleSession] claim failed, allowing session:', error.message, {
          deviceId,
        });
        setStatus('active');
        return true;
      }

      // Anything other than 'claimed' means the deployed claim_active_session is
      // older than this client — enforcement silently stops working, so say so.
      const status = (data as { status?: string } | null)?.status;
      if (status === 'claimed') {
        holdsClaim = true;
        trace('claimed session:', { deviceId, ...(data as object) });
        await readRow('after claim');
      } else {
        console.warn(
          '[SingleSession] unexpected claim result — is the newest-wins migration applied?',
          { deviceId, data },
        );
      }

      setStatus('active');
      return true;
    };

    // Server-side half of the enforcement: revokes the refresh tokens of every
    // other session, so a displaced device cannot keep calling the API even if
    // it never receives the realtime message.
    const revokeOtherSessions = async () => {
      try {
        await supabase.auth.signOut({ scope: 'others' });
      } catch (err) {
        console.warn('[SingleSession] revoking other sessions failed:', err);
      }
    };

    const heartbeat = async () => {
      if (cancelled || doneRef.current) return;
      const { data, error } = await supabase.rpc('touch_active_session', {
        p_device_id: deviceId,
      });
      if (cancelled || error) return;
      trace('heartbeat:', data, { deviceId });

      // 'missing' means nobody holds the row (the other device signed out) —
      // take it rather than signing out.
      if (data === 'missing') {
        await claim();
        return;
      }

      if (data === 'taken') {
        // Never held it: our claim failed or was never confirmed, so this device
        // is not the displaced one. Retry instead of signing ourselves out.
        if (!holdsClaim) {
          console.warn('[SingleSession] claim was never confirmed, retrying:', { deviceId });
          await claim();
          return;
        }

        // Someone else is in the row. Only step aside for a claim made *after*
        // ours — under newest-login-wins, an older claim landing on top of ours
        // is a stale write, and surrendering to it would let the browser we just
        // displaced take the account straight back.
        const row = await readRow('on dispute');
        if (cancelled || doneRef.current) return;

        if (row && claimedAt && row.claimed_at <= claimedAt) {
          console.warn('[SingleSession] older claim overwrote ours, reclaiming:', {
            deviceId,
            ourClaim: claimedAt,
            theirClaim: row.claimed_at,
          });
          await claim();
          return;
        }

        await bounceToAuth({ type: 'evicted' });
      }
    };

    // Instant notification when another device takes the session over.
    const channel = supabase
      .channel(`single-session:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'active_user_sessions',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const holder = (payload.new as { device_id?: string } | null)?.device_id;
          if (!holder || holder === deviceId || !holdsClaim) return;

          // Never sign out straight off a payload. A message can be queued from
          // before our own claim, replayed, or delivered out of order — acting on
          // it directly is what made the newest login evict itself. Treat it as a
          // nudge and let the heartbeat ask the database who actually holds the
          // row right now.
          trace('row changed elsewhere, verifying:', { holder, deviceId });
          heartbeat();
        },
      )
      .subscribe();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') heartbeat();
    };

    (async () => {
      const holding = await claim();
      if (cancelled || !holding) return;
      await revokeOtherSessions();
      if (cancelled) return;
      heartbeatTimer = setInterval(heartbeat, HEARTBEAT_MS);
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', heartbeat);
    })();

    return () => {
      cancelled = true;
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', heartbeat);
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return (
    <SingleSessionContext.Provider value={status}>{children}</SingleSessionContext.Provider>
  );
}

export const useSingleSessionStatus = () => useContext(SingleSessionContext);
